import React, { useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Button from "../../../ui/components/Button";

interface FullscreenProps {
    imageUrl: string;
    projectName?: string;
    username?: string;
    onClose: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    hasNext?: boolean;
    hasPrevious?: boolean;
}

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${Colors.background};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 1000;
`;

const ImageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Image = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const CloseButton = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1001;
`;

const NavigationButton = styled.div<{ position: 'left' | 'right' }>`
    position: absolute;
    ${({ position }) => position}: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
`;

const InfoOverlay = styled.div`
    position: absolute;
    top: 20px;
    left: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    z-index: 1001;
`;

const ProjectName = styled.div`
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 18px;
    color: ${Colors.black};
`;

const Username = styled.div`
    font-family: ${Fonts.lexendLight.fontFamily};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 14px;
    color: ${Colors.black};
`;

const Fullscreen: React.FC<FullscreenProps> = ({
    imageUrl,
    onClose,
    onPrevious,
    onNext,
    hasNext = false,
    hasPrevious = false,
    projectName,
    username,
}) => {
    // Disable scrolling on body when component mounts
    useEffect(() => {
        // Save the original overflow style
        const originalStyle = window.getComputedStyle(document.body).overflow;
        
        // Apply no-scroll style
        document.body.style.overflow = 'hidden';
        
        // Cleanup function to restore original style when component unmounts
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);
    
    return (
        <FullscreenOverlay>
            <CloseButton>
                <Button
                    size="small"
                    color="ghost"
                    icon="delete"
                    onClick={onClose}
                />
            </CloseButton>
            
            {hasPrevious && (
                <NavigationButton position="left">
                    <Button
                        size="small"
                        color="ghost"
                        icon="left"
                        onClick={onPrevious}
                    />
                </NavigationButton>
            )}
            
            <ImageContainer>
                <Image src={imageUrl} alt="Fullscreen view" />
                {(projectName || username) && (
                    <InfoOverlay>
                        {projectName && <ProjectName>{projectName}</ProjectName>}
                        {/* {username && <Username>{username}</Username>} */}
                    </InfoOverlay>
                )}
            </ImageContainer>
            
            {hasNext && (
                <NavigationButton position="right">
                    <Button
                        size="small"
                        color="ghost"
                        icon="right"
                        onClick={onNext}
                    />
                </NavigationButton>
            )}
        </FullscreenOverlay>
    );
};

export default Fullscreen;